import { useFieldApi } from 'informed';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Input } from './Input';
import { OptionsBlock } from './primitives/SelectBlock';
import { ThemeMode } from '@/helpers/enums';
import { mailMask } from '@/helpers/masks';
import { validateEmail } from '@/helpers/validators';

const DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'icloud.com',
  'web.de',
];

export const InputEmail = () => {
  const { t } = useTranslation('common');

  const { setValue, validate, getTouched } = useFieldApi('email');

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const onFocus = () => setFocused(true);
  const onBlur = () => setTimeout(() => setFocused(false), 300);

  const handleSetValue = (value: string) => {
    setValue(value);
    setInputValue(value);
    setFocused(false);
  };

  useEffect(() => {
    if (!focused && getTouched()) validate();
  }, [focused, getTouched, validate]);

  const options = useMemo(() => {
    const inputValueParts = inputValue.replace(/@+/, '@').split('@');
    const inputValueDomain = inputValueParts[1];

    return DOMAINS.filter((domain) => {
      return domain !== inputValueDomain && domain.startsWith(inputValueDomain);
    }).map((domain) => {
      const value = `${inputValueParts[0]}@${domain}`;
      return { name: value, value };
    });
  }, [inputValue]);

  const isOptionsVisible = useMemo(() => {
    const specialSymbolCount = (inputValue.match(/@/g) || []).length;
    return (
      specialSymbolCount < 2 &&
      inputValue.includes('@') &&
      focused &&
      !!options.length
    );
  }, [focused, inputValue, options]);

  return (
    <ScInputEmail>
      <Input
        autoComplete="off"
        placeholder={t('auth.registration.form.email')}
        validateOn="submit"
        name="email"
        validate={validateEmail}
        parser={mailMask}
        onChange={({ target }) => setInputValue(target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {isOptionsVisible && (
        <div className="options">
          <OptionsBlock
            options={options}
            onChange={(provider) => handleSetValue(provider as string)}
            mode={ThemeMode.Light}
          />
        </div>
      )}
    </ScInputEmail>
  );
};

export const ScInputEmail = styled.div`
  position: relative;

  & .options {
    position: absolute;
    z-index: 6;
    left: 0;
    right: 0;
  }
`;
